/**
 * Terrain tab (`ldt-terrain`, spec §13):
 *   - Live altitude stack (AGL / ASL / AMSL)
 *   - Geoid delta banner
 *   - Elevation profile mini-stats
 *   - 4 accordions: Cross-Section · OI Volume · Ground Clearance · Data Sources
 */
import { useEffect } from 'react';
import type { DroneLive } from '@/lib/store/drones';
import { useElevationForDrone, useTerrainAt } from '@/lib/api/terrain';
import { useTerrainSources } from '@/lib/api/queries';
import { getMap } from '@/lib/map/instance';
import { Section, SectionGroup } from '@/components/Section';
import { Accordion } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { InfoRow } from '@/components/InfoRow';
import { MetricCell, MetricRow3 } from '@/components/MetricCell';
import { cn } from '@/lib/utils';
import { droneContextFlags } from '@/lib/droneContextFlags';

const FT_PER_M = 3.28084;
// ENG-271: only the altitude stack and ground clearance are shown for now; the
// rest is hidden behind these flags (see lib/droneContextFlags.ts).
const show = droneContextFlags.terrain;

export function TerrainTab({ drone }: { drone: DroneLive }) {
  const t = drone.telemetry;
  const oi = drone.oi;
  const terrain = useTerrainAt(t.lat, t.lng);
  const elev = useElevationForDrone(drone);
  const sources = useTerrainSources();

  // Mockup parity: Terrain tab centres the map on the drone at a closer
  // zoom so the cross-section makes sense with what's visible.
  useEffect(() => {
    const m = getMap();
    if (!m) return;
    m.flyTo({ center: [t.lng, t.lat], zoom: Math.max(m.getZoom(), 14), duration: 500 });
  }, [drone.id]);

  const gpsFt = t.asl != null ? Math.round(t.asl * FT_PER_M) : null;
  // Geoid offset (ft) sourced from the terrain REST response. baro reads
  // lower by `baro_offset_m` metres (negative value flips sign here).
  const baroOffsetFt =
    terrain.data?.baro_offset_m != null ? Math.round(terrain.data.baro_offset_m * FT_PER_M) : null;
  // The aircraft's own barometric altitude, when it publishes one
  // (`pressure_altitude` on the telemetry topic — present on every tenant and
  // previously read by nothing). Preferred over GPS-plus-a-REST-geoid-offset,
  // which is a derived approximation and goes blank whenever the terrain
  // service is unreachable. Falls back to the derivation.
  const baroFt =
    t.pressure_altitude != null
      ? Math.round(t.pressure_altitude * FT_PER_M)
      : gpsFt != null && baroOffsetFt != null
        ? gpsFt + baroOffsetFt
        : null;
  const delta = gpsFt != null && baroFt != null ? gpsFt - baroFt : null;
  // OI ceiling for the ClearanceBar reference comes from the OI envelope
  // (was hardcoded to 400 ft). Falls back to 400 if backend hasn't supplied it.
  const ceilFt = oi?.agl_ceiling_ft ?? 400;
  // Ground clearance (ENG-271). The terrain service isn't implemented yet, so use
  // the aircraft's own terrain report from the raw GCS stream (height above
  // terrain + terrain elevation) whenever the service has nothing.
  const bareEarth = terrain.data
    ? {
        ft: t.agl ?? 0,
        groundFt: Math.round(terrain.data.ground_elev_m * FT_PER_M),
        badge: 'NRCan HRDEM · DTM',
      }
    : t.terrain_clearance_m != null && t.terrain_elev_m != null
      ? {
          ft: Math.round(t.terrain_clearance_m * FT_PER_M),
          groundFt: Math.round(t.terrain_elev_m * FT_PER_M),
          badge: 'Aircraft terrain report',
        }
      : null;
  // Surface clutter (ft) at the current position.
  const clutterFt =
    terrain.data?.surface_clutter_m != null
      ? Math.round(terrain.data.surface_clutter_m * FT_PER_M)
      : null;

  return (
    <SectionGroup>
      <Section title="Live Altitude Stack">
        <MetricRow3>
          <MetricCell
            label="AGL"
            value={t.agl != null ? `${Math.round(t.agl)} ft` : '—'}
            sub="DTM · Bare Earth"
            tone="ok"
            accent
          />
          <MetricCell
            label="ASL"
            value={gpsFt != null ? `${gpsFt.toLocaleString()} ft` : '—'}
            sub="GPS · WGS-84"
            accentColor="blue"
          />
          <MetricCell
            label="AMSL"
            value={baroFt != null ? `${baroFt.toLocaleString()} ft` : '—'}
            sub="Baro · CGVD2013"
          />
        </MetricRow3>
      </Section>

      {show.deltaWarning && delta != null && Math.abs(delta) <= 25 && (
        <div className="rounded-md border border-(--color-amber)/30 bg-(--color-amber-bg) px-3 py-2">
          <div className="flex items-start gap-2">
            <span className="text-(--color-amber) text-base">⚠</span>
            <div className="flex-1 min-w-0">
              <div className="text-[0.846em] font-bold text-(--color-amber)">
                Autopilot GPS ↔ Baro Delta: {delta >= 0 ? '+' : ''}
                {delta} ft
              </div>
              <div className="text-[0.692em] text-(--color-text2) font-mono mt-0.5">
                Geoid offset (CGVD2013 vs WGS-84) · Within tolerance · RTM validated
              </div>
            </div>
          </div>
        </div>
      )}

      {show.elevationProfile && (
        <Section title="Elevation Profile">
          {elev.isPending && <Skeleton className="h-12 w-full" />}
          {elev.data && (
            <MetricRow3>
              <MetricCell label="Min" value={`${Math.round(elev.data.min_terrain_m)}`} sub="m ASL" />
              <MetricCell
                label="Mean"
                value={`${Math.round((elev.data.min_terrain_m + elev.data.max_terrain_m) / 2)}`}
                sub="m ASL"
                accentColor="blue"
              />
              <MetricCell
                label="Max"
                value={`${Math.round(elev.data.max_terrain_m)}`}
                sub="m ASL"
                tone="crit"
                accent
              />
            </MetricRow3>
          )}
        </Section>
      )}

      {show.crossSection && (
        <Accordion title="Terrain Cross-Section" meta="3D profile">
          <div className="text-[0.769em] text-(--color-text3) py-6 text-center">
            Cross-section canvas renders here when accordion opens (deferred to Sprint B4 per perf
            budget — heavy uPlot render).
          </div>
        </Accordion>
      )}

      {show.oiVolume && (
        <Accordion title="OI Volume Submitted to RTM" meta="WGS84 · ASM 3548">
          {elev.isPending && <Skeleton className="h-32 w-full" />}
          {elev.data && (
            <div className="space-y-2">
              <MetricRow3>
                <MetricCell
                  label="Lowest"
                  value={`${elev.data.min_terrain_m.toFixed(1)} m`}
                  sub={`${(elev.data.min_terrain_m * FT_PER_M).toFixed(0)} ft ASL`}
                  tone="ok"
                />
                <MetricCell
                  label="Highest"
                  value={`${elev.data.max_terrain_m.toFixed(1)} m`}
                  sub={`${(elev.data.max_terrain_m * FT_PER_M).toFixed(0)} ft ASL`}
                  tone="crit"
                />
                <MetricCell
                  label="Variation"
                  value={`${(elev.data.max_terrain_m - elev.data.min_terrain_m).toFixed(1)} m`}
                  sub={`${((elev.data.max_terrain_m - elev.data.min_terrain_m) * FT_PER_M).toFixed(0)} ft`}
                />
              </MetricRow3>
              <div className="rounded-md bg-(--color-bg) p-2.5 border-t-2 border-t-(--color-green-bg) border border-(--color-border)">
                <div className="text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3)">
                  Volume ceiling
                </div>
                <div className="text-[0.923em] font-bold text-(--color-green) font-mono">
                  {elev.data.ceiling_wgs84_m.toFixed(2)} m ASL
                </div>
                <div className="text-[0.692em] text-(--color-text3) font-mono">
                  {(elev.data.ceiling_wgs84_m * FT_PER_M).toFixed(0)} ft · highest terrain + OI AGL
                </div>
                <Badge variant="ok" className="mt-1">
                  FG
                </Badge>
              </div>
              <div className="rounded-md bg-(--color-bg) p-2.5 border border-(--color-border)">
                <div className="text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3)">
                  Volume floor
                </div>
                <div className="text-[0.923em] font-bold text-(--color-green) font-mono">
                  {elev.data.floor_wgs84_m.toFixed(2)} m ASL
                </div>
                <div className="text-[0.692em] text-(--color-text3) font-mono">
                  {(elev.data.floor_wgs84_m * FT_PER_M).toFixed(0)} ft · lowest terrain in polygon
                </div>
              </div>
              <div className="rounded-md bg-(--color-bg) p-2.5 border border-(--color-border)">
                <div className="text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3)">
                  WGS84 · RTM Submission
                </div>
                <InfoRow label="Floor" value={`${elev.data.floor_wgs84_m.toFixed(2)} m`} mono />
                <InfoRow label="Ceiling" value={`${elev.data.ceiling_wgs84_m.toFixed(2)} m`} mono />
                <div className="text-[0.692em] text-(--color-text3) font-mono mt-1">
                  Geoid offset {elev.data.geoid_offset_m.toFixed(2)} m applied · submitted to FLY RTM
                </div>
                <InfoRow
                  label="Volume depth"
                  value={`${(elev.data.ceiling_wgs84_m - elev.data.floor_wgs84_m).toFixed(1)} m · ${((elev.data.ceiling_wgs84_m - elev.data.floor_wgs84_m) * FT_PER_M).toFixed(0)} ft`}
                  valueClassName="text-(--color-violet) font-mono"
                />
              </div>
            </div>
          )}
        </Accordion>
      )}

      <Accordion
        title="Ground Clearance"
        meta={
          bareEarth ? `${bareEarth.ft} ft AGL` : t.agl != null ? `${Math.round(t.agl)} ft AGL` : '—'
        }
      >
        {terrain.isPending && !bareEarth && <Skeleton className="h-32 w-full" />}
        {!terrain.isPending && !terrain.data && !bareEarth && (
          <div className="text-[0.769em] text-(--color-text3) py-3 text-center">
            No ground clearance data for this aircraft yet.
          </div>
        )}
        {(terrain.data || bareEarth) && (
          <div className="space-y-3">
            {bareEarth && (
              <ClearanceBar
                label="Above Bare Earth"
                badge={bareEarth.badge}
                ft={bareEarth.ft}
                groundFt={bareEarth.groundFt}
                ceilFt={ceilFt}
              />
            )}
            {terrain.data && bareEarth && <div className="h-px bg-(--color-border-lite)" />}
            {terrain.data && (
              <>
                <ClearanceBar
                  label="Above Surface Clutter"
                  badge="NRCan MRDEM · DSM"
                  ft={t.agl != null && clutterFt != null ? Math.max(0, t.agl - clutterFt) : 0}
                  groundFt={Math.round(terrain.data.surface_elev_m * FT_PER_M)}
                  ceilFt={ceilFt}
                />
                <div className="rounded-md bg-(--color-bg) p-2 border border-(--color-border-lite) flex items-center justify-between">
                  <span className="text-[0.769em] text-(--color-text2)">
                    Surface clutter at position
                  </span>
                  <span className="text-[0.846em] font-bold font-mono">
                    {clutterFt != null ? `${clutterFt} ft` : '—'}
                    <span className="text-[0.692em] font-normal text-(--color-text3)">
                      {' '}
                      (trees / low structure)
                    </span>
                  </span>
                </div>
              </>
            )}
          </div>
        )}
      </Accordion>

      {show.dataSources && (
        <Accordion
          title="Elevation Data Sources"
          meta={sources.data ? `${sources.data.length} sources` : '—'}
        >
          {sources.isPending && <Skeleton className="h-24 w-full" />}
          {sources.data && (
            <div className="grid grid-cols-2 gap-0 border-t border-l border-(--color-border-lite)">
              {sources.data.map((s) => (
                <SourceTile key={s.name} name={s.name} sub={s.sub} status={s.status} />
              ))}
            </div>
          )}
        </Accordion>
      )}
    </SectionGroup>
  );
}

function ClearanceBar({
  label,
  badge,
  ft,
  groundFt,
  ceilFt,
}: {
  label: string;
  badge: string;
  ft: number;
  groundFt: number;
  ceilFt: number;
}) {
  const pct = Math.min(100, Math.max(0, (ft / ceilFt) * 100));
  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[0.846em] font-bold">{label}</span>
          <Badge variant="muted">{badge}</Badge>
        </div>
        <span className="text-[1em] font-bold font-mono text-(--color-green)">{ft} ft</span>
      </div>
      <div className="text-[0.769em] text-(--color-text3) font-mono">
        Ground elevation: {groundFt.toLocaleString()} ft AMSL
      </div>
      <div className="h-2 bg-(--color-border) rounded mt-1.5 overflow-hidden">
        <div
          className={cn(
            'h-full transition-[width]',
            pct >= 90
              ? 'bg-(--color-red)'
              : pct >= 60
                ? 'bg-(--color-amber)'
                : 'bg-(--color-green)',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex justify-between text-[0.615em] text-(--color-text3) font-mono mt-0.5">
        <span>0 ft</span>
        <span>OI ceiling: {ceilFt} ft AGL</span>
      </div>
    </div>
  );
}

function SourceTile({ name, sub, status }: { name: string; sub: string; status: string }) {
  return (
    <div className="border-r border-b border-(--color-border-lite) p-2">
      <div className="text-[0.769em] font-bold">{name}</div>
      <div className="text-[0.692em] font-mono text-(--color-text3)">{sub}</div>
      <div className="text-[0.692em] font-bold text-(--color-green) mt-0.5">✓ {status}</div>
    </div>
  );
}
